import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main Footer */}
      <div className="section-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img 
                src="/logo-bluelion-claims.png" 
                alt="Blue Lion Claims" 
                className="h-20 w-auto"
              />
            </div>
            <p className="text-primary-foreground/70 text-sm leading-relaxed mb-6">
              Blue Lion Claims is the trading style of Blue Lion Law Limited, 
              a regulated law firm dedicated to helping consumers reclaim 
              overpayments on car finance agreements.
            </p>
            {/* Contact Info */}
            <div className="space-y-3">
              <a
                href="tel:02081530033"
                className="flex items-center gap-3 text-sm text-primary-foreground/70 hover:text-gold transition-colors"
              >
                <Phone className="w-4 h-4 text-gold" />
                0208 153 0033
              </a>
              <a
                href="mailto:contactus@bluelionclaims.co.uk"
                className="flex items-center gap-3 text-sm text-primary-foreground/70 hover:text-gold transition-colors"
              >
                <Mail className="w-4 h-4 text-gold" />
                contactus@bluelionclaims.co.uk
              </a>
              <div className="flex items-start gap-3 text-sm text-primary-foreground/70">
                <MapPin className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                <span>The Centro Buildings, 20-23 Mandela Street, London, NW1 0DU</span>
              </div>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-heading font-semibold text-base mb-5 text-primary-foreground">
              Our Services
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="/gap-insurance-claims" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  GAP Insurance Claims
                </a>
              </li>
              <li>
                <a href="/pcp-car-finance-claims" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  PCP Claims
                </a>
              </li>
              <li>
                <a href="/hp-claims" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  HP Claims
                </a>
              </li>
              <li>
                <a href="/car-finance-mis-selling" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  Car Finance Mis-selling
                </a>
              </li>
              <li>
                <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  Check Eligibility
                </a>
              </li>
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading font-semibold text-base mb-5 text-primary-foreground">
              Company
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="/about" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  About Us
                </a>
              </li>
              <li>
                <a href="/how-it-works" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  How It Works
                </a>
              </li>
              <li>
                <a href="/faq" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  FAQ
                </a>
              </li>
              <li>
                <a href="/contact" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-heading font-semibold text-base mb-5 text-primary-foreground">
              Legal
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="/privacy" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/terms" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  Terms of Business
                </a>
              </li>
              <li>
                <a href="/complaints" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  Complaints Procedure
                </a>
              </li>
              <li>
                <a href="/cookies" className="text-sm text-primary-foreground/70 hover:text-gold transition-colors">
                  Cookie Policy
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Legal Disclaimer Box */}
      <div className="border-t border-primary-foreground/10">
        <div className="section-container py-8">
          <div className="p-6 rounded-xl bg-primary-foreground/5 border border-primary-foreground/10">
            <div className="flex flex-col lg:flex-row items-start gap-6">
              <div className="flex-1 space-y-4">
                <p className="text-xs text-primary-foreground/70 leading-relaxed">
                  BlueLionClaims.co.uk is a trading name of Blue Lion Law Limited, authorised and regulated by the Solicitors Regulation Authority (SRA Number: 815166). Company registered in England and Wales (No. 12276560). Registered office: The Centro Buildings, 20-23 Mandela Street, London, NW1 0DU.
                </p>
                <p className="text-xs text-primary-foreground/70 leading-relaxed">
                  No Win No Fee means you only pay our fees if your claim is successful and you abide by the terms of our agreement. Our fee for a successful claim that is settled without litigation is up to 36% inclusive of VAT. If your claim is settled through litigation, the maximum deduction is capped at 48% inclusive of VAT. Your claim would only be litigated if we believe you will achieve a better outcome. You can cancel without charge within 14 days of instructing us. For cancellations after 14 days, we can charge for our reasonable costs.
                </p>
                <p className="text-xs text-primary-foreground/70 leading-relaxed">
                  You can claim at no cost directly to the lender and then to the Financial Ombudsman Service, without using the services of a law firm or claims management company.
                </p>
                <p className="text-xs text-primary-foreground/70 leading-relaxed">
                  This website uses cookies to improve your experience and for advertising purposes. By continuing to use this site, you consent to our use of cookies. See our <a href="/cookies" className="underline hover:text-primary-foreground">Cookie Policy</a> for details.
                </p>
                <p className="text-xs text-primary-foreground/60 leading-relaxed">
                  The Financial Conduct Authority (FCA) has introduced a free to use motor finance Consumer Redress Scheme. It has indicated that redress may be on average around £830 per agreement. The total estimated amount to be paid to consumers is £7.5b. Details of any finance agreements we find from our checks and investigations will be provided after we have been instructed and we will pursue any claims we identify that have merit.
                </p>
              </div>
              {/* SRA Digital Badge - Official Yoshki Badge */}
              <div 
                className="flex-shrink-0"
                style={{ width: '275px', height: '163px' }}
                dangerouslySetInnerHTML={{
                  __html: `<!-- Start of SRA Digital Badge code -->
<div style="max-width:275px;max-height:163px;"><div style="position: relative;padding-bottom: 59.1%;height: auto;overflow: hidden;"><iframe frameborder="0" scrolling="no" allowTransparency="true" src="https://cdn.yoshki.com/iframe/55845r.html" style="border:0px; margin:0px; padding:0px; backgroundColor:transparent; top:0px; left:0px; width:100%; height:100%; position: absolute;"></iframe></div></div>
<!-- End of SRA Digital Badge code -->`
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="section-container py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-primary-foreground/50">
              &copy; {currentYear} Blue Lion Claims. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-xs text-primary-foreground/50">
                Trading style of Blue Lion Law Limited
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export { Footer };
